import React from 'react'
import { Box, BoxProps, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar } from '../Avatar'

export type PinTone = 'gold' | 'violet' | 'cyan'

export interface PinPosition {
  left: string
  top: string
}

export interface PinProps extends Omit<BoxProps, 'children' | 'position'> {
  tone?: PinTone
  label: React.ReactNode
  /** Anchor point on the frame, as CSS percentages e.g. `{ left: '21%', top: '28%' }` */
  anchor: PinPosition
  /**
   * Soft tone-glow breathe — used in Present/guest when the note is collapsed
   * so the marker stays discoverable without covering the print.
   */
  pulse?: boolean
}

const toneBg: Record<PinTone, string> = {
  gold: '#ffcf7d',
  violet: '#be9dfd',
  cyan: '#01dafe',
}
const toneInk: Record<PinTone, string> = {
  gold: '#4a3510',
  violet: '#2d1a54',
  cyan: '#063a44',
}

const pinPulse = (tone: string) => keyframes`
  0%, 100% {
    box-shadow: 0 4px 14px rgba(0,0,0,0.5), 0 0 16px ${tone}44;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 4px 14px rgba(0,0,0,0.5), 0 0 28px ${tone}aa;
    transform: scale(1.06);
  }
`

const PinRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone' && p !== 'pulse',
})<{ tone: PinTone; pulse?: boolean }>(({ tone, pulse }) => ({
  position: 'absolute',
  width: 26,
  height: 26,
  borderRadius: '50% 50% 50% 4px',
  background: toneBg[tone],
  color: toneInk[tone],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10.5,
  fontWeight: 800,
  boxShadow: `0 4px 14px rgba(0,0,0,0.5), 0 0 22px ${toneBg[tone]}66`,
  ...(pulse && {
    animation: `${pinPulse(toneBg[tone])} 2.4s ease-in-out infinite`,
  }),
}))

/** Absolutely-positioned pin marker anchored to a spot on a photo — pair with `PinNote`. */
export function Pin({ tone = 'gold', label, anchor, pulse, style, ...rest }: PinProps) {
  return (
    <PinRoot tone={tone} pulse={pulse} style={{ left: anchor.left, top: anchor.top, ...style }} {...rest}>
      {label}
    </PinRoot>
  )
}

export interface PinNoteProps extends Omit<BoxProps, 'children' | 'position'> {
  avatarLabel: string
  name: React.ReactNode
  note: React.ReactNode
  /** Anchor point for the note card, as CSS percentages */
  anchor: PinPosition
}

const NoteRoot = styled(Box)({
  position: 'absolute',
  width: 216,
  background: 'var(--ds-night-surface, rgba(23, 22, 29, 0.94))',
  border: '1px solid var(--ds-night-border, rgba(244, 243, 246, 0.1))',
  borderRadius: 12,
  padding: '11px 13px',
  backdropFilter: 'blur(14px)',
  boxShadow: 'var(--ds-shadow-night)',
})

const NoteWho = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  marginBottom: 4,
})

const NoteName = styled('span')({
  fontSize: 11.5,
  fontWeight: 650,
  color: 'var(--ds-night-ink, #fff)',
})

const NoteText = styled(Box)({
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--ds-night-ink-muted, rgba(244, 243, 246, 0.6))',
})

/** Glass note card that hangs off a `Pin` — a pinned comment on a photo. */
export function PinNote({ avatarLabel, name, note, anchor, style, ...rest }: PinNoteProps) {
  return (
    <NoteRoot style={{ left: anchor.left, top: anchor.top, ...style }} {...rest}>
      <NoteWho>
        <Avatar sx={{ width: 18, height: 18, fontSize: 8.5, bgcolor: '#ffcf7d', color: '#4a3510' }}>
          {avatarLabel}
        </Avatar>
        <NoteName>{name}</NoteName>
      </NoteWho>
      <NoteText>{note}</NoteText>
    </NoteRoot>
  )
}
